import React, { useState, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { Backdrop, CircularProgress, TextField, Button, Snackbar, Grid, Typography, Menu, MenuItem, IconButton } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AppConfig, roles, YEAR_COLUMN_DEFS } from "@configs/admin-config";
import { getAccessToken, checkIfRoleExists } from "@utils/oauth";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  root: {},
  border: {
    border: "1px solid #ddd",
  },
  boldText: {
    fontWeight: 500,
  },
  tableHeading: {
    backgroundColor: "#F5F5F5",
  },
  cell1: {
    width: "70%",
    textAlign: "right",
  },
  btnContainer: {
    marginTop: 10,
  },
  submitItem: {
    textAlign: "end",
  },
  btn: {
    textTransform: "none",
  },
  tableTitle: {
    marginBottom: 10,
    fontWeight: 500,
    fontSize: 15,
  },
  tableTitle2: {
    marginTop: 20,
    marginBottom: 10,
    fontWeight: 500,
    fontSize: 15,
  },
  colHeader: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  colHeaderBtn: {
    marginLeft: "auto",
  },
}));
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const WorkingsForRatios = (props) => {
  const { classes } = useStyles();
  const [backdropOpen, setbackDrop] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [year1, setYear1] = useState({});
  const [year2, setYear2] = useState({});
  const [year3, setYear3] = useState({});
  const [isSubmitted, setSubmitted] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [isEditable, setEditable] = useState(true);
  const [tableEmptySnack, setTableEmptySnack] = useState(false);
  const [successSnack, setSuccess] = useState(false);
  const [failSnack, setFail] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  const getCreditScoreItems = async () => {
    if (Object.keys(props.year1).length && Object.keys(props.year2).length && Object.keys(props.year3).length) {
      const usd = props.currency === "US Dollar";
      setYear1({ ...props.year1, exchangeRate: usd ? 1 : parseFloat(props.year1.exchangeRate) });
      setYear2({ ...props.year2, exchangeRate: usd ? 1 : parseFloat(props.year2.exchangeRate) });
      setYear3({ ...props.year3, exchangeRate: usd ? 1 : parseFloat(props.year3.exchangeRate) });
      setDisabled(true);
      setEditable(false);
    } else {
      setYear1({
        companyId: parseInt(props.companyId),
        year: 1,
        currentAssets: 0.0,
        currentLiability: 0.0,
        cash: 0.0,
        investments: 0.0,
        totalDebt: 0.0,
        totalAssets: 0.0,
        revenue: 0.0,
        profit: 0.0,
        exchangeRate: 0.0,
      });
      setYear2({
        companyId: parseInt(props.companyId),
        year: 2,
        currentAssets: 0.0,
        currentLiability: 0.0,
        cash: 0.0,
        investments: 0.0,
        totalDebt: 0.0,
        totalAssets: 0.0,
        revenue: 0.0,
        profit: 0.0,
        exchangeRate: 0.0,
      });
      setYear3({
        companyId: parseInt(props.companyId),
        year: 3,
        currentAssets: 0.0,
        currentLiability: 0.0,
        cash: 0.0,
        investments: 0.0,
        totalDebt: 0.0,
        totalAssets: 0.0,
        revenue: 0.0,
        profit: 0.0,
        exchangeRate: 0.0,
      });
    }
    setLoading(false);
    setbackDrop(false);
  };

  useEffect(() => {
    getCreditScoreItems();
  }, []);

  const handleChange = (year, category) => (e) => {
    setSubmitted(false);
    const value = e.target.value === "" ? "" : parseFloat(e.target.value);
    if (year === 1) setYear1((prev) => ({ ...prev, [category]: value }));
    if (year === 2) setYear2((prev) => ({ ...prev, [category]: value }));
    if (year === 3) setYear3((prev) => ({ ...prev, [category]: value }));
  };

  const edit = () => {
    setDisabled(false);
    setEditable(true);
  };

  const cancel = () => {
    setDisabled(true);
    setEditable(false);
  };

  const submit = async () => {
    setSubmitted(true);
    if (checkObjectComplete(year1) && checkObjectComplete(year2) && checkObjectComplete(year3)) {
      setbackDrop(true);
      setEditable(false);
      setDisabled(true);
      let data = [];

      data.push(year1);
      data.push(year2);
      data.push(year3);

      const requestURL = AppConfig.baseUrl + AppConfig.creditScoreItems;

      try {
        let response = await fetch(requestURL, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + getAccessToken(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
        let body = await response.json();
        setbackDrop(false);
        if (response.status === 200) {
          setSuccess(true);
          props.callBack();
        } else {
          console.error(body.message);
          setFail(true);
        }
        return null;
      } catch (error) {
        console.error(error);
        return error;
      }
    } else {
      setTableEmptySnack(true);
    }
  };

  const handleMenuOpen = (col) => (e) => {
    setAnchorEl(e.currentTarget);
    setActiveMenu(col);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const swapColumns = (colA, colB) => {
    const fields = [
      "currentAssets",
      "currentLiability",
      "cash",
      "investments",
      "totalDebt",
      "totalAssets",
      "revenue",
      "profit",
      "exchangeRate",
    ];
    const getYear = (col) => (col === 1 ? year1 : col === 2 ? year2 : year3);
    const setYearFn = (col) =>
      col === 1 ? setYear1 : col === 2 ? setYear2 : setYear3;
    const dataA = { ...getYear(colA) };
    const dataB = { ...getYear(colB) };
    fields.forEach((field) => {
      const tmp = dataA[field];
      dataA[field] = dataB[field];
      dataB[field] = tmp;
    });
    setYearFn(colA)(dataA);
    setYearFn(colB)(dataB);
    handleMenuClose();
  };

  const checkObjectComplete = (obj) => {
    for (var key in obj) {
      if ((obj[key] === "" || obj[key] === null) && key !== "exchangeRate") return false;
    }
    return true;
  };
  function renderYearHeader({ col, label, swapTargets }) {
    return (
      <TableCell key={col} align="left">
        <div className={classes.colHeader}>
          <span>{label}</span>
          {isEditable && (
            <>
              <IconButton
                id={`col-btn-${col}`}
                size="small"
                className={classes.colHeaderBtn}
                onClick={handleMenuOpen(col)}
                aria-label={`${label} column actions`}
                aria-controls={`col-menu-${col}`}
                aria-haspopup="true"
                aria-expanded={activeMenu === col && Boolean(anchorEl)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                id={`col-menu-${col}`}
                anchorEl={activeMenu === col ? anchorEl : null}
                open={activeMenu === col && Boolean(anchorEl)}
                onClose={handleMenuClose}
                MenuListProps={{ "aria-labelledby": `col-btn-${col}` }}
              >
                {swapTargets.map(({ targetCol, targetLabel }) => (
                  <MenuItem key={targetCol} onClick={() => swapColumns(col, targetCol)}>
                    Swap with {targetLabel}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </div>
      </TableCell>
    );
  }

  function returnCell(year, category) {
    return (
      <TableCell align="left">
        {!isLoading &&
          year === 1 &&
          (disabled ? (
            <span>{year1[category] ? parseFloat(year1[category]) : ""}</span>
          ) : (
            <TextField
              type="number"
              size="small"
              className={classes.cell1}
              onChange={handleChange(year, category)}
              error={isSubmitted && year1[category] === ""}
              inputProps={{ min: 0, style: { textAlign: "end" } }}
              value={year1[category] || ""}
            />
          ))}
        {!isLoading &&
          year === 2 &&
          (disabled ? (
            <span>{year2[category] ? parseFloat(year2[category]) : ""}</span>
          ) : (
            <TextField
              type="number"
              size="small"
              className={classes.cell1}
              onChange={handleChange(year, category)}
              error={isSubmitted && year2[category] === ""}
              inputProps={{ min: 0, style: { textAlign: "end" } }}
              value={year2[category] || ""}
            />
          ))}
        {!isLoading &&
          year === 3 &&
          (disabled ? (
            <span>{year3[category] ? parseFloat(year3[category]) : ""}</span>
          ) : (
            <TextField
              type="number"
              size="small"
              className={classes.cell1}
              onChange={handleChange(year, category)}
              error={isSubmitted && year3[category] === ""}
              inputProps={{ min: 0, style: { textAlign: "end" } }}
              value={year3[category] || ""}
            />
          ))}
      </TableCell>
    );
  }

  function returnCellUSD(year, category) {
    return (
      <TableCell align="left">
        {!isLoading && year === 1 && (
          <span>
            ${" "}
            {(Math.round((parseFloat(year1[category]) / parseFloat(year1["exchangeRate"])) * 100) / 100)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </span>
        )}
        {!isLoading && year === 2 && (
          <span>
            ${" "}
            {(Math.round((parseFloat(year2[category]) / parseFloat(year2["exchangeRate"])) * 100) / 100)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </span>
        )}
        {!isLoading && year === 3 && (
          <span>
            ${" "}
            {(Math.round((parseFloat(year3[category]) / parseFloat(year3["exchangeRate"])) * 100) / 100)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </span>
        )}
      </TableCell>
    );
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setTableEmptySnack(false);
    setSuccess(false);
    setFail(false);
  };

  return (
    <React.Fragment>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={tableEmptySnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          Table can not be Empty
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={successSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          Success
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={failSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          Fail
        </Alert>
      </Snackbar>
      {Object.keys(year1).length > 0 && Object.keys(year2).length > 0 && Object.keys(year3).length > 0 && (
        <div>
          <Typography className={classes.tableTitle}>{props.currency}</Typography>
          <TableContainer className={classes.border}>
            <Table size="small">
              <TableHead className={classes.tableHeading}>
                  <TableRow>
                    <TableCell>Items</TableCell>
                    <TableCell align="left">Currency</TableCell>
                    {YEAR_COLUMN_DEFS.map(renderYearHeader)}
                  </TableRow>
                </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Current Assets</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "currentAssets")}
                  {returnCell(2, "currentAssets")}
                  {returnCell(3, "currentAssets")}
                </TableRow>
                <TableRow>
                  <TableCell>Current Liability</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "currentLiability")}
                  {returnCell(2, "currentLiability")}
                  {returnCell(3, "currentLiability")}
                </TableRow>
                <TableRow>
                  <TableCell>Cash</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "cash")}
                  {returnCell(2, "cash")}
                  {returnCell(3, "cash")}
                </TableRow>
                <TableRow>
                  <TableCell>Investments</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "investments")}
                  {returnCell(2, "investments")}
                  {returnCell(3, "investments")}
                </TableRow>
                <TableRow>
                  <TableCell>Total Debt</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "totalDebt")}
                  {returnCell(2, "totalDebt")}
                  {returnCell(3, "totalDebt")}
                </TableRow>
                <TableRow>
                  <TableCell>Total Assets</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "totalAssets")}
                  {returnCell(2, "totalAssets")}
                  {returnCell(3, "totalAssets")}
                </TableRow>
                <TableRow>
                  <TableCell>Revenue</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "revenue")}
                  {returnCell(2, "revenue")}
                  {returnCell(3, "revenue")}
                </TableRow>
                <TableRow>
                  <TableCell>Profit</TableCell>
                  <TableCell align="left">{props.currency}</TableCell>
                  {returnCell(1, "profit")}
                  {returnCell(2, "profit")}
                  {returnCell(3, "profit")}
                </TableRow>
                {props.currency !== "US Dollar" && (
                  <TableRow>
                    <TableCell>Exchange Rate</TableCell>
                    <TableCell align="left"></TableCell>
                    {returnCell(1, "exchangeRate")}
                    {returnCell(2, "exchangeRate")}
                    {returnCell(3, "exchangeRate")}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {checkIfRoleExists(roles.superuser) && (
            <Grid container spacing={2} className={classes.btnContainer} justify="flex-end">
              <Grid item className={classes.submitItem}>
                {!isEditable && (
                  <Button className={classes.btn} variant="outlined" size="small" color="primary" onClick={edit}>
                    Edit
                  </Button>
                )}
                {isEditable &&
                  Object.keys(year1).length > 0 &&
                  Object.keys(year2).length > 0 &&
                  Object.keys(year3).length > 0 && (
                    <Button className={classes.btn} variant="outlined" size="small" color="primary" onClick={cancel}>
                      Cancel
                    </Button>
                  )}
              </Grid>
              <Grid item className={classes.submitItem}>
                <Button
                  className={classes.btn}
                  variant="contained"
                  size="small"
                  color="secondary"
                  onClick={submit}
                  disabled={!isEditable}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      )}
      {Object.keys(year1).length > 0 &&
        Object.keys(year2).length > 0 &&
        Object.keys(year3).length > 0 &&
        props.currency !== "US Dollar" &&
        checkObjectComplete(year1).toString() === "true" &&
        checkObjectComplete(year2).toString() === "true" &&
        checkObjectComplete(year3).toString() === "true" && (
          <div>
            {!isEditable && (
              <div>
                <Typography className={classes.tableTitle2}>US Dollar</Typography>

                <TableContainer className={classes.border}>
                  <Table size="small">
                    <TableHead className={classes.tableHeading}>
                      <TableCell>Items</TableCell>
                      <TableCell align="left">Currency</TableCell>
                      <TableCell align="left">Year Before (N-2)</TableCell>
                      <TableCell align="left">Prior Year (N-1)</TableCell>
                      <TableCell align="left">Current (N)</TableCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Current Assets</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "currentAssets")}
                        {returnCellUSD(2, "currentAssets")}
                        {returnCellUSD(3, "currentAssets")}
                      </TableRow>
                      <TableRow>
                        <TableCell>Current Liability</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "currentLiability")}
                        {returnCellUSD(2, "currentLiability")}
                        {returnCellUSD(3, "currentLiability")}
                      </TableRow>
                      <TableRow>
                        <TableCell>Cash</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "cash")}
                        {returnCellUSD(2, "cash")}
                        {returnCellUSD(3, "cash")}
                      </TableRow>
                      <TableRow>
                        <TableCell>Investments</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "investments")}
                        {returnCellUSD(2, "investments")}
                        {returnCellUSD(3, "investments")}
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Debt</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "totalDebt")}
                        {returnCellUSD(2, "totalDebt")}
                        {returnCellUSD(3, "totalDebt")}
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Assets</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "totalAssets")}
                        {returnCellUSD(2, "totalAssets")}
                        {returnCellUSD(3, "totalAssets")}
                      </TableRow>
                      <TableRow>
                        <TableCell>Revenue</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "revenue")}
                        {returnCellUSD(2, "revenue")}
                        {returnCellUSD(3, "revenue")}
                      </TableRow>
                      <TableRow>
                        <TableCell>Profit</TableCell>
                        <TableCell align="left">USD</TableCell>
                        {returnCellUSD(1, "profit")}
                        {returnCellUSD(2, "profit")}
                        {returnCellUSD(3, "profit")}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </div>
        )}
    </React.Fragment>
  );
};

export default WorkingsForRatios;
